<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import DeliveriesAPI from 'dashboard/api/notificationDeliveries';

const deliveries = ref([]);
const meta = ref({});
const page = ref(1);
const status = ref('');
const loading = ref(false);

const load = async (p = 1) => {
  loading.value = true;
  try {
    const { data } = await DeliveriesAPI.list({
      page: p,
      per: 50,
      status: status.value || undefined,
    });
    deliveries.value = data.deliveries || [];
    meta.value = data.meta || {};
    page.value = p;
  } catch (e) {
    useAlert('Не удалось загрузить журнал');
  } finally {
    loading.value = false;
  }
};
onMounted(() => load(1));

const fmt = v => (v ? String(v).slice(0, 19).replace('T', ' ').slice(11) : '—');
const countBy = st => deliveries.value.filter(d => d.status === st).length;

const CH = {
  telegram: { label: 'TG', bg: '#e6f6f7', fg: '#0a6f78' },
  sms: { label: 'SMS', bg: '#e7ecff', fg: '#3a4ec0' },
  email: { label: 'EML', bg: '#fbf0db', fg: '#9a6a0f' },
  max: { label: 'MAX', bg: '#efe7fb', fg: '#6b3fb0' },
};
const chip = ch => CH[ch] || { label: (ch || '—').toUpperCase().slice(0, 3), bg: '#eef1f2', fg: '#5b676d' };

const ST = {
  simulated: { label: 'dry-run', color: '#c98a16' },
  sent: { label: 'Доставлено', color: '#1f9d57' },
  responded: { label: 'Прочитано', color: '#0e9ba8' },
  failed: { label: 'Не доставлено', color: '#d14343' },
  queued: { label: 'Ожидание', color: '#c98a16' },
};
const stat = s => ST[s] || { label: s, color: '#5b676d' };
const cascade = d => (d.payload && d.payload._cascade ? d.payload._cascade : null);
</script>

<template>
  <div
    class="w-full h-full overflow-auto bg-n-surface-1"
    style="font-family: 'Golos Text', system-ui, sans-serif"
  >
    <div class="w-full max-w-6xl px-8 py-8 mx-auto">
      <div class="flex items-start justify-between mb-5">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">Логи отправок</h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Журнал доставки по всем каналам. Пока отправка выключена — всё в режиме dry-run.
          </p>
        </div>
      </div>

      <!-- stat pills -->
      <div class="flex flex-wrap gap-2 mb-5">
        <span class="px-3 py-1.5 text-sm rounded-full" style="background: #fbf3e0; color: #9a6a0f">
          ● dry-run {{ countBy('simulated') }}
        </span>
        <span class="px-3 py-1.5 text-sm rounded-full" style="background: #e7f7ee; color: #1f7a47">
          ● Доставлено {{ countBy('sent') }}
        </span>
        <span class="px-3 py-1.5 text-sm rounded-full" style="background: #e6f6f7; color: #0a6f78">
          ● Прочитано {{ countBy('responded') }}
        </span>
        <span class="px-3 py-1.5 text-sm rounded-full" style="background: #fbe9e9; color: #b53232">
          ● Не доставлено {{ countBy('failed') }}
        </span>
        <span class="self-center ml-2 text-sm text-n-slate-10">всего: {{ meta.total }}</span>
      </div>

      <div class="overflow-hidden bg-white border rounded-xl border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs tracking-wide uppercase border-b border-n-weak text-n-slate-10">
              <th class="px-4 py-3 font-medium text-left">Время</th>
              <th class="px-4 py-3 font-medium text-left">Сценарий</th>
              <th class="px-4 py-3 font-medium text-left">Пациент</th>
              <th class="px-4 py-3 font-medium text-left">Канал</th>
              <th class="px-4 py-3 font-medium text-left">Каскад</th>
              <th class="px-4 py-3 font-medium text-left">Статус</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in deliveries" :key="d.id" class="border-b border-n-weak last:border-0">
              <td class="px-4 py-3 tabular-nums text-n-slate-11">{{ fmt(d.created_at) }}</td>
              <td class="px-4 py-3 font-medium text-n-slate-12">
                {{ d.scenario ? d.scenario.name : '—' }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">{{ d.contact ? d.contact.name : '—' }}</td>
              <td class="px-4 py-3">
                <span
                  class="px-1.5 py-0.5 text-xs font-semibold rounded"
                  :style="{ background: chip(d.channel).bg, color: chip(d.channel).fg }"
                >
                  {{ chip(d.channel).label }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-n-slate-10">
                <span v-if="cascade(d)">{{ cascade(d).join(' → ') }}</span>
                <span v-else>—</span>
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center gap-1.5" :style="{ color: stat(d.status).color }">
                  <span class="w-1.5 h-1.5 rounded-full" :style="{ background: stat(d.status).color }"></span>
                  {{ stat(d.status).label }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!loading && !deliveries.length" class="py-10 text-center text-n-slate-11">
          Отправок пока нет. Активные сценарии будут писать сюда строки (dry-run).
        </div>
      </div>

      <div class="flex items-center justify-center gap-3 mt-4">
        <button
          class="px-3 py-1.5 text-sm border rounded-lg border-n-weak text-n-slate-11 disabled:opacity-40"
          :disabled="page <= 1 || loading"
          @click="load(page - 1)"
        >
          ‹ Назад
        </button>
        <span class="text-sm text-n-slate-11">Стр. {{ page }}</span>
        <button
          class="px-3 py-1.5 text-sm border rounded-lg border-n-weak text-n-slate-11 disabled:opacity-40"
          :disabled="deliveries.length < 50 || loading"
          @click="load(page + 1)"
        >
          Вперёд ›
        </button>
      </div>
    </div>
  </div>
</template>
